// @flow
import i18n from "i18next";
import k from "src/i18n/keys";

import { useSelector, useDispatch } from "react-redux";
import React, { useEffect, useCallback, useContext } from "react";
import * as R from "ramda";

import {
  Filter as StyledFilter,
  ListItem,
  SubHeading,
  Priority as StyledPriority,
  Separator,
} from "./styles";
import { FilterWorkerContext } from "src/contexts/FilterWorkerContext";
import { filterEmbeddedFields } from "src/actions/workflows";
import {
  getChecklistFieldsById,
  getAllRecords,
  getAllWorkflows,
} from "src/reducers";
import Checkbox from "src/components/Checkbox";
import { Capitalize } from "src/styles";
import Icon from "src/icons";

type Props = {
  parentRef: any,
  handleClose: Function,
  setFilter: Function,
  toggleSortBy: Function,
  filter: Array<string>,
};

const column = "priority";

const Priority = ({
  parentRef,
  handleClose,
  setFilter,
  filter,
  toggleSortBy,
}: Props) => {
  const dispatch = useDispatch();
  const filterWorker = useContext(FilterWorkerContext);
  const workflow = useSelector(({ app }) => getAllWorkflows(app));
  const fieldsById = useSelector(({ app }) => getChecklistFieldsById(app));
  const allRecords = useSelector(({ app }) => getAllRecords(app));
  const filters = workflow.instanceFilter;
  const chatroomAttributes = ["owner", "dueDate", "status"];
  const instancesById = workflow.instancesById;
  const updatedInstances = { ...instancesById };

  let workerData = {
    workflow,
    fieldsById: fieldsById.toJS(),
    instancesById,
    filters,
    chatroomAttributes,
    updatedInstances,
    allRecords,
  };

  const closeModal = useCallback(
    (event: any) => {
      if (event.keyCode === 13) {
        handleClose();
      }
    },
    [handleClose],
  );

  const clickOutside = useCallback(
    (event: any) => {
      if (parentRef && parentRef.current) {
        if (!parentRef.current.contains(event.target)) {
          handleClose();
        }
      }
    },
    [parentRef, handleClose],
  );

  useEffect(() => {
    document.addEventListener("keydown", closeModal, false);
    document.addEventListener("click", clickOutside, false);
    return () => {
      document.removeEventListener("keydown", closeModal, false);
      document.removeEventListener("click", clickOutside, false);
    };
  }, []);

  const handleSort = (ascending: boolean) => {
    if (ascending) {
      toggleSortBy(false, true);
    } else {
      toggleSortBy(true, true);
    }

    handleClose();
  };

  const handleSelect = useCallback(
    (event: Event, item: string | number) => {
      event.preventDefault();
      event.stopPropagation();

      if (R.includes(item, filter || [])) {
        workerData = {
          ...workerData,
          payload: {
            ...filters,
            [column]: R.reject(R.equals(item), filter),
          },
        };

        if (filterWorker) {
          filterWorker.postMessage(workerData, (result) => {
            dispatch(filterEmbeddedFields({ instances: result }));
          });
        }

        setFilter(column, R.reject(R.equals(item), filter));
      } else {
        workerData = {
          ...workerData,
          payload: {
            ...filters,
            [column]: [...(filter || []), item],
          },
        };

        if (filterWorker) {
          filterWorker.postMessage(workerData, (result) => {
            dispatch(filterEmbeddedFields({ instances: result }));
          });
        }

        setFilter(column, [...(filter || []), item]);
      }
    },
    [filter, setFilter],
  );

  const values = [
    i18n.t(k.LOW),
    i18n.t(k.NORMAL1),
    i18n.t(k.HIGH),
    i18n.t(k.CRITICAL),
  ];

  const preventDefault = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <StyledFilter onClick={preventDefault}>
      <SubHeading>{i18n.t(k.SORT_BY2)}</SubHeading>
      <ul>
        <ListItem tabIndex="0" role="button" onClick={() => handleSort(true)}>
          {i18n.t(k.ASCENDING)}
        </ListItem>
        <ListItem tabIndex="0" role="button" onClick={() => handleSort(false)}>
          {i18n.t(k.DESCENDING)}
        </ListItem>
      </ul>
      <Separator />
      <SubHeading>{i18n.t(k.FILTER)}</SubHeading>
      <ul>
        {values.map((value) => (
          <StyledPriority
            tabIndex="0"
            role="button"
            key={value}
            onClick={(event) => handleSelect(event, value)}
          >
            <Checkbox
              id={`${i18n.t(k.PRIORITY)}${value}`}
              checked={R.includes(value, filter || [])}
              handleChange={(event) => handleSelect(event, value)}
            />

            <Icon type={value} />
            <Capitalize>{value}</Capitalize>
          </StyledPriority>
        ))}
      </ul>
    </StyledFilter>
  );
};

export default Priority;
